import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { axiosGetV3, axiosPost } from '../../api/axios-handler';
import { LogoSquare } from '../../assets';

export const resourceKeys = {
  all: ['resources'] as const,
  resource: ['resource'] as const,
};

export interface Resource {
  client_id: number | null;
  description: string;
  id: number;
  image_url: string;
  name: string;
  notes: string;
  phone_number: string;
  sort_weight: number;
  status: string;
  url: string;
  url_description: string;
  subject_areas: number[] | null;
  challenge_areas: number[] | null;
  long_description?: string;
  tag_names: string[];
  tag_group_names?: string[];
  wide_image_url: string;
  rating?: number;
  match_reason?: 'name' | 'tag' | 'bio' | 'story' | 'related' | 'title' | 'summary' | 'transcript';
  match_snippet?: string;
  match_strength?: 'strong' | 'related';
  matched_story_title?: string;
}

export interface ResourcesResponse {
  data: Resource[];
  count: number;
}

interface UseResourcesParams {
  clientId?: number;
  showGlobal?: boolean;
}

export const useResources = ({ clientId, showGlobal }: UseResourcesParams) => {
  return useQuery<ResourcesResponse>({
    queryKey: ['resources', clientId],
    queryFn: () =>
      axiosGetV3('/resources', { client_id: clientId, show_global: showGlobal }).then((response) => response.data),
    staleTime: Infinity,
    select: (data: ResourcesResponse) => {
      return {
        ...data,
        data: data.data.map((resource) => {
          resource.image_url = resource.image_url || LogoSquare;
          return resource;
        }),
      };
    },
  });
};

export const useResourceRating = () => {
  const queryClient = useQueryClient();
  const mutation = useMutation(
    ({ rating, resourceId }: { rating: number; resourceId: number }) => {
      return axiosPost(`/resources/${resourceId}/favorite`, { rating }, 'v3');
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['resources']);
        queryClient.invalidateQueries(['resourcesByTopic']);
        queryClient.invalidateQueries(['textSearchResources']);
        queryClient.invalidateQueries([resourceKeys.resource]);
      },
      onMutate: ({ rating, resourceId }) => {
        // Optimistic update for resources queries
        const updateResourceRating = <T extends { id: number }>(item: T): T =>
          item.id === resourceId ? { ...item, rating } : item;

        // Update all resources queries
        queryClient.setQueriesData<ResourcesResponse>({ queryKey: ['resources'] }, (data) => {
          if (!data) return;
          return {
            ...data,
            data: data.data.map(updateResourceRating),
          };
        });

        // Update all resourcesByTopic queries (with any parameters)
        queryClient.setQueriesData<ResourcesResponse>({ queryKey: ['resourcesByTopic'] }, (data) => {
          if (!data) return;
          return {
            ...data,
            data: data.data.map(updateResourceRating),
          };
        });

        // Update all textSearchResources queries
        queryClient.setQueriesData<ResourcesResponse>({ queryKey: ['textSearchResources'] }, (data) => {
          if (!data) return;
          return {
            ...data,
            data: data.data.map(updateResourceRating),
          };
        });

        // Update individual resource query
        queryClient.setQueryData<Resource>([resourceKeys.resource, resourceId], (data) => {
          if (!data) return;
          return { ...data, rating };
        });
      },
    },
  );

  return {
    ...mutation,
  };
};
